import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Banner,
    BannerDescription,
    BannerEyebrow,
    BannerImage,
    BannerImageContainer,
    BannerLinks,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    BasicResponsiveImage,
    BoundedContent,
    Box,
    FluidImage,
    Icon,
    IconButton,
} from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import SlidePickers from '../../../../components/SlidePickers/SlidePickers';
import BoxWithCloudBackground from '../BoxWithCloudBackground/BoxWithCloudBackground';
import BusinessNameInput from './BusinessNameInput';
import styles, { stylesheet } from './HeroBanner.module.scss';
import HeroFloatingItems from './HeroFloatingItems';
import type { HeroSlides } from './HeroFloatingItems';

const HERO_SLIDES: HeroSlides = {
    slide1: {
        src: 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/vista-logos-pages/en-US/logomaker-gen-ai/hero/SBO_1/',
        hero: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 1 > SBO Image.src',
        alt: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 1 > SBO Image.altText',
        heroAspectRatio: 1472 / 1572,
        floatingItems: [
            { name: 'sbo_1_KeyRing', className: 'floatingItem1' },
            { name: 'sbo_1_Ball', className: 'floatingItem2 sbo1' },
            { name: 'sbo_1_Font1', className: 'floatingItem3 sbo1' },
            { name: 'sbo_1_DogBed', className: 'floatingItem4 sbo1' },
            { name: 'sbo_1_DogBowl', className: 'floatingItem5 sbo1' },
            { name: 'sbo_1_PetToy', className: 'floatingItem6' },
            { name: 'sbo_1_PetProduct', className: 'floatingItem8 sbo1' },
            { name: 'sbo_1_PetProduct2', className: 'floatingItem9' },
        ],
        logoItem: {
            name: 'sbo_1_logo_1',
            src: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 1 > Logo Image.src',
            className: 'floatingItem7 logoContainer',
        },
        item2Index: 1,
    },
    slide2: {
        src: 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/vista-logos-pages/en-US/logomaker-gen-ai/hero/SBO_2/',
        hero: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 2 > SBO Image.src',
        alt: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 2 > SBO Image.altText',
        heroAspectRatio: 1472 / 1572,
        floatingItems: [
            { name: 'sbo_2_PieChart', className: 'floatingItem1' },
            { name: 'sbo_2_PiggyBank', className: 'floatingItem2 sbo2' },
            { name: 'sbo_2_Image', className: 'floatingItem3 sbo2' },
            { name: 'sbo_2_Calculator', className: 'floatingItem4 sbo2' },
            { name: 'sbo_2_DonutChart', className: 'floatingItem5 sbo2' },
            { name: 'sbo_2_Folder', className: 'floatingItem8 sbo2' },
            { name: 'sbo_2_font2', className: 'floatingItem9 sbo2' },
        ],
        logoItem: {
            name: 'sbo_2_logo',
            src: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 2 > Logo Image.src',
            className: 'floatingItem7 logoContainer',
        },
        item2Index: 1,
    },
    slide3: {
        src: 'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/vista-logos-pages/en-US/logomaker-gen-ai/hero/SBO_3/',
        hero: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 3 > SBO Image.src',
        alt: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 3 > SBO Image.altText',
        heroAspectRatio: 1472 / 1572,
        floatingItems: [
            { name: 'sbo_3_HairOil', className: 'floatingItem1 sbo3' },
            { name: 'sbo_3_Beads', className: 'floatingItem2 sbo1' },
            { name: 'sbo_3_Font3', className: 'floatingItem3 sbo3' },
            { name: 'sbo_3_HairDryer', className: 'floatingItem4 sbo3' },
            { name: 'sbo_3_Mirror', className: 'floatingItem5 sbo3' },
            { name: 'sbo_3_Comb', className: 'floatingItem8 sbo3' },
            { name: 'sbo_3_Scissors', className: 'floatingItem9 sbo3' },
        ],
        logoItem: {
            name: 'sbo_3_logo',
            src: 'Logomaker GenAi Landing Page > Hero Banner > Carousel 3 > Logo Image.src',
            className: 'floatingItem7 logoContainer',
        },
        item2Index: 1,
    },
};

const SLIDE_INTERVAL = 8000;
const STAGGER_DELAY = 100;
const ZOOM_ANIMATION_DURATION = 500;

const HeroBanner = () => {
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
    const [zoomingOutItems, setZoomingOutItems] = useState<Set<number>>(new Set());
    const [isLogoVisible, setIsLogoVisible] = useState(false);
    const [isLogoZoomingOut, setIsLogoZoomingOut] = useState(false);
    const [hasTransitioned, setHasTransitioned] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const pauseButtonRef = useRef<HTMLButtonElement>(null);

    const slides = [HERO_SLIDES.slide1, HERO_SLIDES.slide2, HERO_SLIDES.slide3];
    const currentSlide = slides[currentIndex];
    const floatingItemsCount = currentSlide.floatingItems.length;

    const getHeroImageClassName = (): string => {
        if (isTransitioning) return styles.slideOut;
        if (hasTransitioned) return styles.slideIn;
        return '';
    };

    const updateVisibility = (indices: number[], action: 'show' | 'hide' | 'zoomOut') => {
        if (action === 'show') {
            setVisibleItems((prev) => new Set([...Array.from(prev), ...indices]));
        } else if (action === 'zoomOut') {
            setZoomingOutItems((prev) => new Set([...Array.from(prev), ...indices]));
        } else if (action === 'hide') {
            setVisibleItems(
                (prev) => new Set(Array.from(prev).filter((i) => !indices.includes(i))),
            );
            setZoomingOutItems(
                (prev) => new Set(Array.from(prev).filter((i) => !indices.includes(i))),
            );
        }
    };

    const runTransition = useCallback(
        (nextIndex: number) => {
            const nextSlide = slides[nextIndex];
            const itemIndices = Array.from({ length: floatingItemsCount }, (_, i) => i);
            const logoStartTime = itemIndices.length * STAGGER_DELAY;
            const switchTime = logoStartTime + ZOOM_ANIMATION_DURATION;

            // 1. Zoom out floating items sequentially (0.1s apart)
            itemIndices.forEach((itemIdx, i) => {
                setTimeout(() => updateVisibility([itemIdx], 'zoomOut'), i * STAGGER_DELAY);
                setTimeout(
                    () => updateVisibility([itemIdx], 'hide'),
                    i * STAGGER_DELAY + ZOOM_ANIMATION_DURATION,
                );
            });

            // 2. After floating items start zooming out, trigger Logo zoom out + Hero slide out
            setTimeout(() => {
                setIsLogoZoomingOut(true);
                setIsTransitioning(true);
            }, logoStartTime);

            // 3. Switch slide content when out-animations finish
            setTimeout(() => {
                setIsLogoZoomingOut(false);
                setIsLogoVisible(false);
                setCurrentIndex(nextIndex);
                setIsTransitioning(false);
                setHasTransitioned(true);

                // 4. Zoom in Logo + Item 2 + Hero (Simultaneous)
                setIsLogoVisible(true);
                updateVisibility([nextSlide.item2Index], 'show');

                // 5. Zoom in remaining items sequentially
                const remainingItems = Array.from(
                    { length: nextSlide.floatingItems.length },
                    (_, i) => i,
                ).filter((idx) => idx !== nextSlide.item2Index);
                remainingItems.forEach((itemIdx, i) => {
                    setTimeout(() => updateVisibility([itemIdx], 'show'), (i + 1) * STAGGER_DELAY);
                });
            }, switchTime);
        },
        [currentIndex, floatingItemsCount, slides],
    );

    const togglePause = () => {
        setIsPaused(!isPaused);
    };

    useEffect(() => {
        const initialItems = Array.from(
            { length: currentSlide.floatingItems.length },
            (_, i) => i,
        ).filter((idx) => idx !== currentSlide.item2Index);

        setIsLogoVisible(true);
        updateVisibility([currentSlide.item2Index], 'show');
        initialItems.forEach((itemIdx, i) => {
            setTimeout(() => updateVisibility([itemIdx], 'show'), (i + 1) * STAGGER_DELAY);
        });
    }, []);

    useEffect(() => {
        if (isPaused) {
            return;
        }
        intervalRef.current = setInterval(() => {
            runTransition((currentIndex + 1) % slides.length);
        }, SLIDE_INTERVAL);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [currentIndex, runTransition, slides.length, isPaused]);

    const handleSlidePickerClick = (index: number) => {
        if (index !== currentIndex) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            runTransition(index);
        }
    };

    useStyles(stylesheet);

    return (
        <BoundedContent paddingX={0} as="section" id="banner">
            <BoxWithCloudBackground variation="hero">
                <Box className={styles.bannerBox}>
                    <Banner padding={0} style={{ backgroundColor: 'unset', zIndex: 1 }}>
                        <BannerTextContainer paddingX={{ xs: 5, md: 5 }}>
                            <BannerText>
                                <BannerEyebrow fontSkin="body-standard-bold">
                                    {t('Logomaker GenAi Landing Page > Hero > Eyebrow.value')}
                                </BannerEyebrow>
                                <BannerTitle as="h1" className={styles.bannerTitle}>
                                    {t('Logomaker GenAi Landing Page > Hero > Title.value')}
                                </BannerTitle>
                                <BannerDescription as="p" fontSkin="body-standard">
                                    {t('Logomaker GenAi Landing Page > Hero > Description.value')}
                                </BannerDescription>
                                <BannerLinks>
                                    <BusinessNameInput
                                        setIsPaused={setIsPaused}
                                        pauseButtonRef={pauseButtonRef}
                                    />
                                </BannerLinks>
                            </BannerText>
                        </BannerTextContainer>
                        <BannerImageContainer display={{ xs: 'none', sm: 'block' }}>
                            <BannerImage>
                                <BasicResponsiveImage
                                    aspectRatio={currentSlide.heroAspectRatio}
                                    alt={t(currentSlide.alt, '')}
                                    src={t(currentSlide.hero)}
                                    className={`${styles.sboImage} ${getHeroImageClassName()}`}
                                />
                            </BannerImage>
                        </BannerImageContainer>
                    </Banner>

                    <Box
                        className={styles.sboImageMobile}
                        style={{ position: 'relative', zIndex: 1 }}
                    >
                        <FluidImage
                            alt={t(currentSlide.alt)}
                            src={t(currentSlide.hero)}
                            className={getHeroImageClassName()}
                        />
                    </Box>

                    {currentSlide.floatingItems.map((item, index) => {
                        return (
                            <HeroFloatingItems
                                key={`floating-${item.name}-${currentIndex}`}
                                item={item}
                                isVisible={visibleItems.has(index) || zoomingOutItems.has(index)}
                                isZoomingOut={zoomingOutItems.has(index)}
                                isPaused={isPaused}
                                src={`${currentSlide.src}${item.name}.webp`}
                            />
                        );
                    })}

                    <HeroFloatingItems
                        key={`logo-${currentSlide.logoItem.name}-${currentIndex}`}
                        item={currentSlide.logoItem}
                        isVisible={isLogoVisible || isLogoZoomingOut}
                        isZoomingOut={isLogoZoomingOut}
                        isPaused={isPaused}
                        src={t(currentSlide.logoItem.src)}
                    />
                    <Box className={styles.slidePickersContainer}>
                        <SlidePickers
                            slideCount={slides.length}
                            currentSlide={currentIndex}
                            onClick={handleSlidePickerClick}
                        />
                    </Box>
                </Box>
                <IconButton
                    ref={pauseButtonRef}
                    className={styles.pauseButton}
                    tooltip={
                        isPaused
                            ? t('Logomaker GenAi Landing Page > Hero > Play Button > Tooltip.value')
                            : t(
                                  'Logomaker GenAi Landing Page > Hero > Pause Button > Tooltip.value',
                              )
                    }
                    onClick={() => togglePause()}
                >
                    <Icon iconType={isPaused ? 'play' : 'pause'} />
                </IconButton>
            </BoxWithCloudBackground>
        </BoundedContent>
    );
};

export default HeroBanner;
